import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, X, History } from 'lucide-react';
import { getSectionLabel } from '@/lib/sectionLabels';

const LAST_SECTION_KEY = 'fbc_pref_last_visited_section';
const DISMISS_SESSION_KEY = 'fbc_resume_banner_dismissed';

/**
 * "Resume where you left off" banner.
 *
 * Shown on the home landing page when the user previously visited a meaningful
 * (non-home) section and hasn't dismissed the banner in the current session.
 * Reads `fbc_pref_last_visited_section` from localStorage — written by MainApp
 * whenever activeSection changes to a non-home value.
 *
 * Props:
 *   user     — AuthContext user (banner only renders when logged in)
 *   onResume — (sectionId) => void  called when user clicks "Continue"
 */
export default function ResumeBanner({ user, onResume }) {
  const [lastSection, setLastSection] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      if (sessionStorage.getItem(DISMISS_SESSION_KEY) === '1') {
        setDismissed(true);
        return;
      }
      const raw = localStorage.getItem(LAST_SECTION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string' && parsed && parsed !== 'home') {
        setLastSection(parsed);
      }
    } catch {
      // noop — if storage is unavailable, banner simply doesn't show.
    }
  }, [user]);

  const label = getSectionLabel(lastSection);

  if (!user || !lastSection || !label || dismissed) return null;

  const handleResume = () => {
    onResume?.(lastSection);
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_SESSION_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className="container mx-auto px-4 mt-24 lg:mt-20"
        data-testid="resume-banner"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <History className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Pick up where you left off
              </p>
              <p className="text-sm font-medium truncate" data-testid="resume-banner-label">
                {label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={handleResume}
              className="gap-1.5"
              data-testid="resume-banner-continue-btn"
            >
              Continue
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-md hover:bg-muted transition-colors"
              aria-label="Dismiss"
              data-testid="resume-banner-dismiss-btn"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
